import React, { useState, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage } from '@redpanda/ui';
import { Toaster } from 'react-hot-toast';
import './i18n';
import DashboardPage from './pages/admin/DashboardPage';
import QueueManagementPage from './pages/admin/QueueManagementPage';
import SettingsPage from './pages/admin/SettingsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import ProfilePage from './pages/admin/ProfilePage';
import AdminPage from './pages/admin/AdminPage';
import ManualPage from './pages/admin/ManualPage';
import DisplayBoardPage from './pages/public/DisplayBoardPage';
import TakeNumberPage from './pages/public/TakeNumberPage';
import MyTicketPage from './pages/public/MyTicketPage';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { Bars3Icon } from '@heroicons/react/24/outline';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();

  // 判斷是否為公開頁面（不需認證）
  const isPublicRoute = location.pathname.startsWith('/q/') ||
                        location.pathname.startsWith('/ticket/') ||
                        location.pathname.startsWith('/display/');

  if (isLoading && !isPublicRoute) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex">
      {/* 手機版漢堡按鈕 */}
      {isAuthenticated && !isPublicRoute && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </button>
      )}

      {/* Sidebar */}
      {isAuthenticated && !isPublicRoute && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 主要內容區域 */}
      <div className={`flex-1 flex flex-col ${isAuthenticated && !isPublicRoute ? 'md:ml-48' : ''}`}>
        <main className="flex-grow">
          <Routes>
            {/* 管理後台（需登入） */}
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<DashboardPage />} />
              <Route path="/admin/queue" element={<QueueManagementPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin/profile" element={<ProfilePage />} />
              <Route path="/admin/admin" element={<AdminPage />} />
              <Route path="/admin/manual" element={<ManualPage />} />
            </Route>

            {/* 公開頁面（不需登入） */}
            <Route path="/q/:storeId" element={<TakeNumberPage />} />
            <Route path="/ticket/:token" element={<MyTicketPage />} />
            <Route path="/display/:storeId" element={<DisplayBoardPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

      </div>
      <Toaster position="bottom-right" />
    </div>
  );
};

const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ColorThemeProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;
